import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PrescriptionFulfillmentStepper } from "@/components/clinical/PrescriptionFulfillmentStepper";
import { printPrescriptionRemainder } from "@/components/clinical/PrescriptionRemainderPrintSheet";
import { PageHeader } from "@/components/PageHeader";
import { usePageMeta } from "@/context/PageMetaContext";
import { getBillingDocument } from "@/core/billing/billing-documents/billing-documents-store";
import { BILLING_DOC_STATUS_LABELS } from "@/core/billing/billing-documents/types";
import { getPrescriptionById, updatePrescriptionLine } from "@/core/clinical/clinical-store";
import {
  canDeliverLine,
  canTransferLineToBilling,
  deliverPrescriptionLines,
  lineStatusSummary,
  listRemainderLines,
  markPrescriptionLinesExternal,
  transferPrescriptionLinesToBilling,
  validatePrescriptionStockLines,
} from "@/core/clinical/prescription-fulfillment";
import {
  PRESCRIPTION_STATUS_LABELS,
  formatPrescriptionPosology,
} from "@/core/clinical/types";
import {
  getCoverageCaseForPrescription,
  prescriptionLineRequiresInsurerValidation,
} from "@/core/insurance/coverage-case-flow";
import {
  type FulfillmentStep,
  getStepHelp,
  lineMatchesStep,
  resolveFulfillmentStep,
} from "@/core/clinical/prescription-fulfillment-steps";
import { pharmacyInventoryApi } from "@/core/inventory/inventory-api";
import { IscApiError } from "@/core/inventory/isc-client";
import { isApiOfflineError, resolveProductPrice } from "@/core/inventory/pharmacy-stock-sync";
import { getPatientById } from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";
import type { StockProduct } from "@/core/pharmacy/types";
import { formatMoney } from "@/core/pharmacy/format";
import { routes } from "@/router/all_routes";

export function PharmacyOrderDetailPage() {
  const { prescriptionId = "" } = useParams();
  const [refreshKey, setRefreshKey] = useState(0);
  const [products, setProducts] = useState<StockProduct[]>([]);
  const [stockAvailable, setStockAvailable] = useState<Map<string, number>>(new Map());
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [activeStep, setActiveStep] = useState<FulfillmentStep>("stock");

  const prescription = useMemo(() => {
    void refreshKey;
    return getPrescriptionById(prescriptionId);
  }, [prescriptionId, refreshKey]);

  const patient = useMemo(
    () => (prescription ? getPatientById(prescription.patientId) : null),
    [prescription],
  );

  usePageMeta(
    patient
      ? {
          pageTitle: `Ordonnance — ${patientFullName(patient)}`,
          breadcrumbLabel: patientFullName(patient),
        }
      : null,
  );

  const coverageCase = useMemo(
    () => (prescription ? getCoverageCaseForPrescription(prescription.id) : null),
    [prescription],
  );

  const reloadCatalog = useCallback(async () => {
    try {
      const [list, balances] = await Promise.all([
        pharmacyInventoryApi.listPharmacyProducts(),
        pharmacyInventoryApi.listStockBalances(),
      ]);
      setProducts(list.filter((p) => p.status === "ACTIVE"));
      setStockAvailable(new Map(balances.map((b) => [b.productId, b.quantityAvailable])));
      setLoadError(null);
    } catch (err) {
      setLoadError(
        err instanceof IscApiError && err.status === 403
          ? "Accès catalogue refusé."
          : isApiOfflineError(err)
            ? "API stock inaccessible."
            : "Catalogue inaccessible.",
      );
      setProducts([]);
    }
  }, []);

  useEffect(() => {
    void reloadCatalog();
  }, [reloadCatalog]);

  useEffect(() => {
    const refresh = () => setRefreshKey((k) => k + 1);
    window.addEventListener("hospitol:billing-documents", refresh);
    window.addEventListener("hospitol:clinical-prescriptions", refresh);
    return () => {
      window.removeEventListener("hospitol:billing-documents", refresh);
      window.removeEventListener("hospitol:clinical-prescriptions", refresh);
    };
  }, []);

  const refresh = () => setRefreshKey((k) => k + 1);

  const toggleLine = (lineId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  };

  const remainderLines = useMemo(
    () => (prescription ? listRemainderLines(prescription.id) : []),
    [prescription, refreshKey],
  );

  const suggestedStep = useMemo(
    () => (prescription ? resolveFulfillmentStep(prescription) : "stock"),
    [prescription, refreshKey],
  );

  useEffect(() => {
    setActiveStep(suggestedStep);
  }, [suggestedStep]);

  if (!prescription || !patient) {
    return (
      <div className="alert alert-warning">
        Ordonnance introuvable.{" "}
        <Link to={routes.pharmacyOrders}>Retour à la file</Link>
      </div>
    );
  }

  const canValidateStock =
    prescription.status === "sent_to_pharmacy" || prescription.status === "pharmacist_validated";

  const selectedIds = [...selected];

  const handleValidateStock = async () => {
    setBusy(true);
    setLoadError(null);
    try {
      const targets =
        selectedIds.length > 0 ? selectedIds : prescription.lines.map((l) => l.id);
      const result = await validatePrescriptionStockLines(prescription.id, targets);
      const parts: string[] = [];
      if (result.reserved.length > 0) {
        parts.push(`${result.reserved.length} ligne(s) réservée(s)`);
      }
      if (result.shortage.length > 0) {
        parts.push(`${result.shortage.length} en rupture`);
      }
      setMsg(parts.join(" · ") || "Aucune ligne traitée.");
      setSelected(new Set());
      refresh();
      void reloadCatalog();
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Réservation stock échouée.");
    } finally {
      setBusy(false);
    }
  };

  const handleTransfer = async () => {
    setBusy(true);
    setLoadError(null);
    try {
      const targets =
        selectedIds.length > 0
          ? selectedIds
          : prescription.lines
              .filter((l) => canTransferLineToBilling(prescription.id, l.id).ok)
              .map((l) => l.id);
      const doc = await transferPrescriptionLinesToBilling(prescription.id, targets);
      if (!doc) throw new Error("Aucune ligne éligible pour la comptabilité.");
      setMsg(`Comptabilité — dossier ${doc.number} (${doc.payerType === "insurer" ? "assureur" : "patient"}).`);
      setSelected(new Set());
      refresh();
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Transfert échoué.");
    } finally {
      setBusy(false);
    }
  };

  const handleDeliver = async () => {
    setBusy(true);
    setLoadError(null);
    try {
      const targets =
        selectedIds.length > 0
          ? selectedIds
          : prescription.lines
              .filter((l) => canDeliverLine(prescription.id, l.id).ok)
              .map((l) => l.id);
      const result = await deliverPrescriptionLines(prescription.id, targets);
      if (result.delivered.length === 0) {
        throw new Error(result.failed[0]?.reason ?? "Aucune ligne livrable.");
      }
      setMsg(`${result.delivered.length} ligne(s) livrée(s) — stock mis à jour.`);
      if (result.failed.length > 0) {
        setLoadError(
          result.failed.map((f) => f.reason).join(" · "),
        );
      }
      setSelected(new Set());
      refresh();
      void reloadCatalog();
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Livraison échouée.");
    } finally {
      setBusy(false);
    }
  };

  const handlePrintRemainder = async () => {
    const lines =
      selectedIds.length > 0
        ? prescription.lines.filter(
            (l) => selectedIds.includes(l.id) && l.fulfillment.stockState === "shortage",
          )
        : remainderLines;
    if (lines.length === 0) {
      setLoadError("Sélectionnez des lignes en rupture de stock.");
      return;
    }
    printPrescriptionRemainder(prescription, patient, lines);
    await markPrescriptionLinesExternal(
      prescription.id,
      lines.map((l) => l.id),
    );
    setMsg("Ordonnance restante imprimée — lignes marquées achat externe.");
    setSelected(new Set());
    refresh();
  };

  const handleQtyChange = (lineId: string, quantity: number) => {
    if (prescription.status !== "sent_to_pharmacy") return;
    updatePrescriptionLine(prescription.id, lineId, { quantity: Math.max(1, quantity) });
    refresh();
  };

  return (
    <div>
      <PageHeader
        title={`Ordonnance — ${patientFullName(patient)}`}
        description={`Dispensation · ${PRESCRIPTION_STATUS_LABELS[prescription.status]}`}
        actions={
          <Link to={routes.pharmacyOrders} className="btn btn-outline-secondary btn-sm">
            File ordonnances
          </Link>
        }
      />

      {msg && <div className="alert alert-success py-2">{msg}</div>}
      {loadError && <div className="alert alert-warning py-2">{loadError}</div>}

      {coverageCase && (
        <div className="ph-invoice-sheet mb-3 small">
          <span className="text-muted">Prise en charge assureur :</span>{" "}
          <span className="fw-semibold">{coverageCase.status}</span>
          {coverageCase.status === "pending" && (
            <span className="text-warning ms-2">
              — validez dans Assurance avant facturation des lignes concernées.
            </span>
          )}
        </div>
      )}

      <div className="ph-invoice-sheet mb-4 ph-invoice-sheet--flat">
        <PrescriptionFulfillmentStepper
          prescription={prescription}
          activeStep={activeStep}
          onStepChange={setActiveStep}
        />
      </div>

      <div className="ph-invoice-sheet mb-4">
        <h3 className="h6 fw-semibold mb-3">Lignes à délivrer</h3>
        <div className="table-responsive ph-diag-table">
          <table className="table table-sm ph-dash-table align-middle mb-0">
            <thead>
              <tr>
                <th style={{ width: 32 }} />
                <th>Médicament</th>
                <th>Posologie</th>
                <th>Stock dispo</th>
                <th>Prix</th>
                <th>Qté</th>
                <th>État</th>
              </tr>
            </thead>
            <tbody>
              {prescription.lines.map((line) => {
                const product = products.find((p) => p.id === line.productId);
                const price = product ? (resolveProductPrice(product) ?? 0) : 0;
                const available = stockAvailable.get(line.productId);
                const needsInsurer = prescriptionLineRequiresInsurerValidation(
                  prescription.id,
                  line.id,
                );
                const transferCheck = canTransferLineToBilling(prescription.id, line.id);
                const deliverCheck = canDeliverLine(prescription.id, line.id);
                const billingDoc = line.fulfillment.billingDocumentId
                  ? getBillingDocument(line.fulfillment.billingDocumentId)
                  : null;

                return (
                  <tr
                    key={line.id}
                    className={`ph-diag-row ph-fulfill-row${
                      lineMatchesStep(prescription, line, activeStep) ? " ph-fulfill-row--focus" : ""
                    }`}
                  >
                    <td className="ph-diag-no-label">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selected.has(line.id)}
                        onChange={() => toggleLine(line.id)}
                        disabled={line.fulfillment.dispenseState === "delivered"}
                      />
                    </td>
                    <td data-label="Médicament">
                      {line.productLabel}
                      {needsInsurer && (
                        <span className="badge bg-secondary ms-1 small">Validation</span>
                      )}
                    </td>
                    <td data-label="Posologie" className="small text-muted">
                      {formatPrescriptionPosology(line)}
                    </td>
                    <td data-label="Stock dispo" className="small">
                      {available !== undefined ? (
                        <>
                          {available} dispo
                          {line.fulfillment.stockState === "shortage" && (
                            <span className="text-danger d-block">Rupture</span>
                          )}
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td data-label="Prix" className="small">
                      {formatMoney(price)}
                    </td>
                    <td data-label="Qté" style={{ maxWidth: 90 }}>
                      <input
                        type="number"
                        min={1}
                        className="form-control form-control-sm"
                        value={line.quantity}
                        disabled={
                          prescription.status !== "sent_to_pharmacy" ||
                          line.fulfillment.stockState === "reserved"
                        }
                        onChange={(e) =>
                          handleQtyChange(line.id, Number(e.target.value) || 1)
                        }
                      />
                    </td>
                    <td data-label="État" className="small">
                      <div>{lineStatusSummary(line)}</div>
                      {billingDoc && (
                        <div className="text-muted">
                          {BILLING_DOC_STATUS_LABELS[billingDoc.status]}
                        </div>
                      )}
                      {!transferCheck.ok &&
                        line.fulfillment.stockState === "reserved" &&
                        !line.fulfillment.billingDocumentId && (
                          <div className="text-muted">{transferCheck.reason}</div>
                        )}
                      {deliverCheck.ok && (
                        <span className="badge bg-success">Prêt à livrer</span>
                      )}
                      {!deliverCheck.ok &&
                        line.fulfillment.billingDocumentId &&
                        line.fulfillment.dispenseState !== "delivered" && (
                          <div className="text-muted">{deliverCheck.reason}</div>
                        )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="ph-fulfill-step-panel">
        <p className="ph-fulfill-step-help">{getStepHelp(activeStep)}</p>
        <div className="d-flex flex-wrap gap-2 align-items-center ph-fulfill-step-actions">
          {activeStep === "stock" && canValidateStock && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={busy}
              onClick={() => void handleValidateStock()}
            >
              {busy ? "…" : "Valider le stock"}
            </button>
          )}
          {activeStep === "stock" && remainderLines.length > 0 && (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={busy}
              onClick={() => void handlePrintRemainder()}
            >
              Imprimer ordonnance restante
            </button>
          )}
          {activeStep === "billing" && (
            <>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={busy}
                onClick={() => void handleTransfer()}
              >
                {busy ? "…" : "Envoyer comptabilité"}
              </button>
              <Link to={routes.billingAccounting} className="btn btn-outline-primary btn-sm">
                Ouvrir comptabilité
              </Link>
            </>
          )}
          {activeStep === "delivery" && (
            <>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={busy}
                onClick={() => void handleDeliver()}
              >
                {busy ? "…" : "Livrer les médicaments"}
              </button>
              <Link to={routes.billingCashier} className="btn btn-outline-secondary btn-sm">
                Caisse
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
